import { motion } from 'framer-motion'
import {
  PaintBrushIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const features = [
  { title: 'Custom responsive design', desc: 'Pixel-perfect websites that look stunning on every device.' },
  { title: 'SEO optimization', desc: 'Built-in SEO best practices for maximum search visibility.' },
  { title: 'Performance optimization', desc: 'Lightning-fast load times with optimized assets and caching.' },
  { title: 'CMS integration', desc: 'Easy content management with headless or traditional CMS.' },
]

const providers = [
  { name: 'WordPress', url: 'https://wordpress.org', desc: 'World\'s most popular CMS' },
  { name: 'Webflow', url: 'https://webflow.com', desc: 'Visual web design platform' },
  { name: 'Shopify', url: 'https://www.shopify.com', desc: 'E-commerce website builder' },
  { name: 'Next.js', url: 'https://nextjs.org', desc: 'React framework for production' },
]

export const WebDesignPage = () => {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-metallic-900">Web Design</h1>
        <p className="text-metallic-500 mt-1">Professional website design and development with modern aesthetics.</p>
      </motion.div>

      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((f) => (
          <Card key={f.title} hover>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <PaintBrushIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-metallic-900">{f.title}</h3>
                <p className="text-sm text-metallic-500 mt-1">{f.desc}</p>
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4">Service Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {providers.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 rounded-xl border border-metallic-200 hover:border-purple-300 hover:bg-purple-50 transition-all group">
                <div>
                  <p className="font-medium text-metallic-900 group-hover:text-purple-600">{p.name}</p>
                  <p className="text-sm text-metallic-500">{p.desc}</p>
                </div>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-metallic-400 group-hover:text-purple-500" />
              </a>
            ))}
          </div>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-r from-purple-50 to-white border-purple-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className="w-6 h-6 text-purple-600" />
            <h3 className="font-semibold text-metallic-900">What's Included</h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {['Custom domain setup', 'SSL certificate', 'Mobile-first design', 'Contact forms', 'Analytics integration', '24/7 support'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-metallic-600">
                <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </motion.div>
  )
}
